import React, { ReactElement, memo, useMemo, useCallback } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { AIP_FLOW_THEME_OVERRIDES, formatQuantityLabel, useFlowThemeOverride } from "@swiggy-private/checkout-core";
import { TEXTS } from "@cart-ui/constants";
import {
    QUICK_CHECKOUT_CART_PAYMENT_FOOTER_ACCESSIBILITY_TEXTS,
    QUICK_CHECKOUT_CART_PAYMENT_FOOTER_TEXTS,
} from "@cart-ui/constants/QuickCheckoutCartPaymentFooter";

// helpers
import { format, ScreenReaderOnly, useEffectOnce } from "@swiggy-private/core-ui";

// test ids
import { CART_FOOTER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import { Footer, FullWidthPayButton, Title, Subtitle, Container, LoaderWrapper, TextDivider } from "./styles";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

export interface QuickCheckoutCartPaymentFooterProps {
    cartTotal: number;
    totalItemCount?: number;
    tipAmount?: number;
    makePayment: () => void;
    impressionProceedToPay: Function;
    /** Shows a loader on the pay CTA and blocks clicks while a cart update is in flight. */
    updateCartInProgress?: boolean;
}

function QuickCheckoutCartPaymentFooter(props: QuickCheckoutCartPaymentFooterProps): ReactElement {
    const { updateCartInProgress = false } = props;
    const accessibilityText = useMemo(() => {
        return `${format(QUICK_CHECKOUT_CART_PAYMENT_FOOTER_ACCESSIBILITY_TEXTS.CART_TOTAL, String(props.cartTotal))} ${
            props.tipAmount && props.tipAmount > 0 ? format(TEXTS.WITH_X_TIP, String(props.tipAmount)) : ""
        }`;
    }, [props.cartTotal, props.tipAmount]);

    const onMakePaymentClick = useCallback(() => {
        if (updateCartInProgress) {
            return;
        }
        props.makePayment();
    }, [props, updateCartInProgress]);

    useEffectOnce(() => {
        props.impressionProceedToPay();
    });

    const { customTheme, cssVariables, baseTheme } = useFlowThemeOverride({
        flow: "AIP",
        overridesByFlow: AIP_FLOW_THEME_OVERRIDES,
    });

    return (
        <Footer>
            <ScreenReaderOnly text={accessibilityText} />
            <Container>
                <ThemeProvider
                    theme={customTheme}
                    colorMode={baseTheme.colorMode}
                    design_language={baseTheme.designLanguage}
                >
                    <div style={cssVariables}>
                        <FullWidthPayButton
                            variant="positive"
                            onClick={updateCartInProgress ? undefined : onMakePaymentClick}
                            data-testid={CART_FOOTER_TEST_IDS.MAKE_PAYMENT}
                            isLoading={updateCartInProgress}
                        >
                            {updateCartInProgress ? (
                                <LoaderWrapper data-testid={CART_FOOTER_TEST_IDS.MAKE_PAYMENT_LOADER}>
                                    <Loader
                                        size="medium"
                                        type="primary"
                                        foregroundColor="text_Button_Primary"
                                        backgroundColor="text_inverse_disabled"
                                    />
                                </LoaderWrapper>
                            ) : (
                                <>
                                    <Title>
                                        {!isEmpty(props.totalItemCount) && (
                                            <>
                                                <Text
                                                    variant="V2.body1.neutral.bold"
                                                    color="text_Button_Primary"
                                                    as="span"
                                                >
                                                    {formatQuantityLabel(
                                                        props.totalItemCount ?? 0,
                                                        QUICK_CHECKOUT_CART_PAYMENT_FOOTER_TEXTS.ITEM_LABEL,
                                                    )}
                                                </Text>
                                                <TextDivider
                                                    variant="V2.body1.neutral.regular"
                                                    color="text_inverse_Med_Emphasis"
                                                >
                                                    {"|"}
                                                </TextDivider>
                                            </>
                                        )}
                                        <Text variant="V2.body1.neutral.bold" color="text_Button_Primary" as="span">
                                            {QUICK_CHECKOUT_CART_PAYMENT_FOOTER_TEXTS.PAY}{" "}
                                            <Text variant="V2.body1.neutral.bold" color="text_Button_Primary" as="span">
                                                {`₹${props.cartTotal}`}
                                            </Text>
                                        </Text>
                                    </Title>
                                    <Subtitle>
                                        <Text
                                            as="span"
                                            variant="V2.overline.neutral.bold"
                                            color="special_Price_AIP_Final_Price"
                                        >
                                            {QUICK_CHECKOUT_CART_PAYMENT_FOOTER_TEXTS.FINAL_PRICE}
                                        </Text>
                                    </Subtitle>
                                </>
                            )}
                        </FullWidthPayButton>
                    </div>
                </ThemeProvider>
            </Container>
        </Footer>
    );
}

export default memo(QuickCheckoutCartPaymentFooter);
